import { useCallback, useContext, useMemo } from "react";

import { ProjectRead } from "@nens/rana/api-v1";
import { TenantContext } from "@nens/ranalib";
import { useSessionStorage } from "@nens/react/hooks";

import { FILTER_CONTRIBUTOR_ID_KEY } from "@/config/query-parameter-consts";
import { useRanaTableData } from "@/hooks/router/use-rana-table-data";
import { useTenantActionAllowed } from "@/hooks/use-action-allowed-in-tenant";
import { useContributorDropdown } from "@/hooks/use-contributor-dropdown";
import { useProjectListQuery } from "@/queries/query-project";
import { getSort } from "@/utils/get-sort";

function filterProject(
  project: ProjectRead,
  searchString: string,
  filterByUserId?: string | null
): boolean {
  if (filterByUserId && project.contributors.every(({ id }) => id !== filterByUserId)) return false;
  if (!searchString) return true;
  return (
    project.name.toLowerCase().includes(searchString.toLowerCase()) ||
    project.code.toLowerCase().includes(searchString.toLowerCase())
  );
}

export function useTenantProjectListData() {
  const authInfo = useContext(TenantContext);
  const user = !authInfo.isPublic ? authInfo.user : undefined;
  const { canCreateProject } = useTenantActionAllowed();
  const [showingMap, setShowingMap] = useSessionStorage("rana.projects_map.show", true);
  const [selectedUserId, setSelectedUserId] = useSessionStorage<string | null>(
    FILTER_CONTRIBUTOR_ID_KEY,
    user?.id ?? null
  );
  const {
    searchString,
    setSearchString,
    setOrdering,
    orderings,
    page,
    offset,
    limit,
    setPageSize,
    setPage,
    nextPage,
    previousPage,
  } = useRanaTableData({
    keyAddon: "projectList",
    initialOrderings: { lastActivity: "za" },
    initialPage: 1,
    initialPageSize: showingMap ? 10 : 20,
  });
  const projectsResponse = useProjectListQuery();
  const projects = useMemo(() => projectsResponse?.data?.items ?? [], [projectsResponse]);
  const contributorDropdown = useContributorDropdown({
    projects,
    selectedUserId,
    setSelectedUserId,
    setPage,
  });
  const filteredProjects = useMemo(
    () => projects.filter((project) => filterProject(project, searchString, selectedUserId)),
    [projects, searchString, selectedUserId]
  );
  const sortedProjects = useMemo(
    () =>
      filteredProjects.slice().sort((projectA, projectB) => {
        const orderingKey = Object.keys(orderings)[0] as "createdAt" | "updatedAt" | "name";
        return getSort(projectA[orderingKey], projectB[orderingKey], orderings[orderingKey]);
      }),
    [filteredProjects, orderings]
  );
  const projectsOnPage = sortedProjects.slice(offset, offset + limit);
  const showMap = useCallback(() => setShowingMap(true), [setShowingMap]);
  const hideMap = useCallback(() => setShowingMap(false), [setShowingMap]);
  return {
    hasProjects: projects.length > 0,
    projects,
    filteredProjects,
    sortedProjects,
    projectsOnPage,
    orderings,
    setOrdering,
    isLoading: projectsResponse.isLoading,
    searchString,
    setSearchString,
    showingMap,
    showMap,
    hideMap,
    canCreateProject,
    page,
    offset,
    limit,
    setPageSize,
    numPages: Math.ceil(sortedProjects.length / limit),
    setPage,
    previousPage,
    nextPage,
    contributorDropdown,
  };
}
